import { useCallback, useEffect, useRef, useState } from "react";

interface UseCountdownOptions {
  onComplete?: () => void;
  initialSeconds?: number;
}

interface UseCountdownReturn {
  countdown: string;
  startCountdown: (durationSeconds: number) => void;
  isCountdownActive: boolean;
}

export const useCountdown = (
  options?: UseCountdownOptions,
): UseCountdownReturn => {
  const { onComplete, initialSeconds = 0 } = options ?? {};

  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);
  const intervalRef = useRef<number | null>(null);

  const clear = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startCountdown = useCallback(
    (durationSeconds: number) => {
      clear();
      setSecondsLeft(durationSeconds);

      intervalRef.current = window.setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clear();
            onComplete?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [onComplete],
  );

  useEffect(() => {
    if (initialSeconds > 0) {
      startCountdown(initialSeconds);
    }
    return clear;
  }, []);

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  const countdown =
    minutes === 0
      ? String(seconds)
      : `${minutes}:${String(seconds).padStart(2, "0")}`;

  return {
    countdown,
    startCountdown,
    isCountdownActive: secondsLeft > 0,
  };
};
